/**
 * XMLDropzone — F-008 Phase 6 (IMPL-P6-002).
 *
 * Dropzone OPZIONALE per il file XML Entratel collegato alla ricevuta.
 *
 * Scope F-008 (V1):
 *  - Solo state lato client — nessuno storage/upload XHR backend.
 *  - Il file viene tenuto in memoria locale; il caller può scegliere di
 *    persisterlo in futuro (campo `xmlS3Key` nel commit input è opzionale e
 *    rimane null in V1).
 *  - Esposto come `<details>` collassato di default per ridurre rumore visivo
 *    sull'happy-path (il PDF è il flusso principale).
 *
 * Validazione client-side:
 *  - MIME: `application/xml` o `text/xml`
 *  - Size: max 5 MiB (vincolo soft V1, half del limite PDF)
 *
 * Accessibilità:
 *  - `<details>` + `<summary>` nativi (keyboard + screen reader supportati)
 *  - `aria-label` sul wrapper file input
 *  - errori via `role="alert"`
 */
import { useRef, useState } from 'react';
import { FileCheck, AlertCircle } from 'lucide-react';

const MAX_XML_SIZE = 5 * 1024 * 1024; // 5 MiB

export interface XMLDropzoneProps {
  /** Chiamato quando un file XML valido è selezionato. */
  onFileSelected?: (file: File) => void;
  /** Disabilita il dropzone (es. mentre il commit è in flight). */
  disabled?: boolean;
}

export function XMLDropzone({ onFileSelected, disabled }: XMLDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function handleFile(file: File) {
    setErrorMsg(null);
    // MIME guard — alcuni browser inviano stringhe vuote, accettiamo anche .xml extension.
    const isXmlMime =
      file.type === 'application/xml' ||
      file.type === 'text/xml' ||
      (file.type === '' && /\.xml$/i.test(file.name));
    if (!isXmlMime) {
      setErrorMsg('Selezionare un file XML');
      return;
    }
    if (file.size > MAX_XML_SIZE) {
      setErrorMsg('Il file supera il limite di 5 MB');
      return;
    }
    setFileName(file.name);
    onFileSelected?.(file);
  }

  return (
    <details className="rounded-md border bg-background">
      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">
        Carica file XML Entratel (opzionale)
      </summary>
      <div className="space-y-2 px-3 pb-3 pt-1">
        <p className="text-xs text-muted-foreground">
          Allegato facoltativo: il file XML originale generato da Entratel. In
          V1 viene memorizzato solo localmente (nessun upload server-side).
        </p>
        <input
          ref={inputRef}
          type="file"
          accept="application/xml,text/xml,.xml"
          aria-label="Carica file XML Entratel (opzionale)"
          disabled={disabled}
          className="block w-full text-sm file:mr-2 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-muted/80 disabled:opacity-50"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        {fileName && !errorMsg && (
          <div className="flex items-center gap-2 text-green-600">
            <FileCheck className="h-4 w-4" aria-hidden="true" />
            <span className="text-xs">{fileName}</span>
          </div>
        )}
        {errorMsg && (
          <div
            role="alert"
            className="flex items-center gap-2 text-destructive"
          >
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <span className="text-xs">{errorMsg}</span>
          </div>
        )}
      </div>
    </details>
  );
}
